import {
  Component,
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
} from 'solid-js';
import {
  PointCloudClassification,
  PointCloudStylingIntensity,
} from '@trimble/types';
import { PointCloudClassificationControlProps } from './point-cloud-classification.model';
import { PointCloudRgbControl } from '../point-cloud-rgb/point-cloud-rgb';
import { applyColorInputStyle } from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { BtnClasses, CustomTooltip, IconButton } from '@tcweb/connect-ui';

export const PointCloudClassificationControl: Component<
  PointCloudClassificationControlProps
> = (props: PointCloudClassificationControlProps) => {
  applyColorInputStyle();

  const [blendIntensity, setBlendIntensity] = createSignal<boolean>(false);

  createEffect(() => {
    const blendIntensity = props.blendIntensity;
    if (blendIntensity !== undefined) {
      setBlendIntensity(blendIntensity);
    }
  });

  const displayPercentage = createMemo(() => {
    return (
      props.classifications &&
      props.classifications.some((c) => c.percentage > 0)
    );
  });

  const toggleVisibility = (item: PointCloudClassification): void => {
    if (props.onClassificationChange) {
      props.onClassificationChange({ ...item, visible: !item.visible });
    }
  };

  const toggleColor = (item: PointCloudClassification, color: string): void => {
    if (props.onClassificationChange) {
      props.onClassificationChange({ ...item, color: color });
    }
  };

  const handleRgbChange = (rgbStyling: PointCloudStylingIntensity): void => {
    if (
      rgbStyling.blendIntensity !== undefined &&
      rgbStyling.blendIntensity !== blendIntensity()
    ) {
      setBlendIntensity(rgbStyling.blendIntensity);
      props.onBlendIntensityToggle?.(rgbStyling.blendIntensity);
    }

    if (
      rgbStyling.gradient !== undefined &&
      rgbStyling.gradient !== props.intensityGradient
    ) {
      props.onIntensityGradientChange?.(rgbStyling.gradient);
    }
  };

  return (
    <Show when={props.classifications && props.classifications.length > 0}>
      <div class="flex flex-1 flex-col gap-4">
        <PointCloudRgbControl
          blendIntensity={blendIntensity()}
          onChange={handleRgbChange}
        />

        <div class="flex flex-col gap-2">
          <span class="flex flex-1 font-semibold">
            {t('mobileMapping.styles.classification.styling')}
          </span>
          <div class="border-t border-gray-0">
            <For each={props.classifications}>
              {(item) => (
                <div class="hover:bg-modus-background-hover py-1 gap-1 flex flex-row items-center border-b border-gray-0">
                  <IconButton
                    data-cy="classification-visibility-icon"
                    color={BtnClasses.TERTIARY}
                    icon={item.visible ? 'visibility_on' : 'visibility_off'}
                    size="icon-small"
                    clicked={() => {
                      toggleVisibility(item);
                    }}
                  />

                  <div class="flex flex-1 overflow-hidden">
                    <CustomTooltip
                      text={`${item.id}. ${item.label}`}
                      position="bottom"
                      class="overflow-hidden"
                      tooltipClass="!max-w-fit"
                    >
                      <span
                        data-testid="classification-label"
                        class="block truncate"
                      >
                        {item.id}. {item.label}
                      </span>
                    </CustomTooltip>
                  </div>

                  <Show when={displayPercentage()}>
                    <span class="flex w-10 text-right">
                      {item.percentage < 1 ? '<1' : item.percentage}%
                    </span>
                  </Show>
                  <input
                    id="head"
                    name="head"
                    type="color"
                    value={item.color}
                    onInput={(e) => {
                      const color = (e.target as HTMLInputElement).value;
                      toggleColor(item, color);
                    }}
                  />
                </div>
              )}
            </For>
          </div>
        </div>
      </div>
    </Show>
  );
};
